import { createRouter, createWebHashHistory } from "vue-router";

// Backend (Dashboard / admin style)
import LayoutBackend from "@/layouts/v2/AppSidebarLayout.vue";

// Store(s)
import { useAuthUserStore } from "@/stores/auth";
import { useChartTypeStore } from "@/stores/chartType";
import { useItemSettingsStore } from "@/stores/itemSettings";
import { useSessionStorage } from "@vueuse/core";

// AUTH
const Login = () => import("@/views/auth/Login.vue");
const SelectCompany = () => import("@/views/auth/SelectCompany.vue");
const ForgotPassword = () => import("@/views/auth/ForgotPassword.vue");
const ResetPassword = () => import("@/views/auth/ResetPassword.vue");

// PAGES
const LandingPage = () => import("@/views/LandingPage.vue");
const BasePageRenderer = () => import("@/views/reports/BasePageRenderer.vue");

// DASHBOARDS

const DashboardRenderer = () => import("@/views/dashboards/DashboardRenderer.vue");

// Backend Admin
const Users = () => import("@/views/backend/admin/Users.vue");
const Tenants = () => import("@/views/backend/admin/Tenants.vue");
const VariableLibrary = () => import("@/views/backend/admin/VariableLibrary.vue");
const ReferenceLists = () => import("@/views/backend/admin/ReferenceLists.vue");

// Backend: Dashboard
const Reports = () => import("@/views/backend/Reports.vue");
const Dashboards = () => import("@/views/backend/Dashboards.vue");
const Merchants = () => import("@/views/backend/data/Merchants.vue");
const RateCard = () => import("@/views/backend/data/RateCard.vue");
const LineItemReview = () => import("@/views/backend/data/LineItemReview.vue");
const Pipelines = () => import("@/views/backend/data/Pipelines.vue");
const DataExplorer = () => import("@/views/backend/data/DataExplorer.vue");
const Templates = () => import("@/views/backend/Templates.vue");
const DashboardItems = () => import("@/views/backend/DashboardItems.vue");
// Routes on settings.version: pre-v3 charts open in the legacy builder.
const ChartBuilder = () => import("@/views/backend/builder/v3/ChartBuilderSurface.vue");
const Queries = () => import("@/views/backend/Queries.vue");
const QueryBuilder = () => import("@/views/backend/builder/v2/QueryBuilder.vue");
const PageBuilder = () => import("@/views/backend/builder/v2/PageBuilder.vue");
const DashboardBuilder = () => import("@/views/backend/builder/v2/DashboardBuilder.vue");
const Standardise = () => import("@/views/backend/Standardise.vue");
const Categorise = () => import("@/views/backend/Categorise.vue");
const Classify = () => import("@/views/backend/Classify.vue");
const InterchangeFees = () => import("@/views/backend/data/InterchangeFees.vue");
// const Pages = () => import("@/views/backend/Pages.vue");
const Files = () => import("@/views/backend/data/Files.vue");


// Set all routes
const routes = [
  {
    path: "/",
    component: LayoutBackend,
    children: [
      {
        path: '',
        component: LandingPage,
        name: 'landing',
        meta: { requiresAuth: true },
      }
    ]
    
  },

  {
    path: "/login",
    name: "login",
    component: Login,
  },

  {
      path: "/forgot-password",
      name: "forgot-password",
      component: ForgotPassword,
  },

  {
      path: "/reset-password",
      name: "reset-password",
      component: ResetPassword,
  },


  {
    path: "/select-company",
    name: "select-company",
    meta: { requiresAuth: true },
    component: SelectCompany,
  },

  {
    path: "/backend",
    component: LayoutBackend,
    meta: { requiresAuth: true },
    children: [

      {
        path: "admin/users",
        name: "users",
        component: Users,
        meta: { requiresAuth: true }
      },

      {
        path: "admin/tenants",
        name: "tenants",
        component: Tenants,
        meta: { requiresAuth: true }
      },

      {
        path: "admin/variables",
        name: "variable-library",
        component: VariableLibrary,
        meta: { requiresAuth: true }
      },

      {
        path: "admin/reference-lists",
        name: "reference-lists",
        component: ReferenceLists,
        meta: { requiresAuth: true }
      },

      {
        path: "reports",
        name: "reports",
        component: Reports,
        meta: { requiresAuth: true }
      },

      {
        path: "dashboards",
        name: "dashboards",
        component: Dashboards,
        meta: { requiresAuth: true }
      },

      {
        path: "templates",
        name: "templates",
        component: Templates,
        meta: { requiresAuth: true }
      },

      {
        path: "dashboard-items",
        name: "dashboard-items",
        component: DashboardItems,
        meta: { requiresAuth: true}
      },

      {
        path: "queries",
        name: "queries",
        component: Queries,
        meta: { requiresAuth: true }
      },

      {
        path: "query-builder/:queryId?",
        name: "query-builder",
        component: QueryBuilder,
        meta: { requiresAuth: true }
      },

      // {
      //   path: "chart-builder/:dashboardItemId?",
      //   name: "chart-builder",
      //   component: ChartBuilder,
      //   meta: { requiresAuth: true }
      // },

      // Temp route
      {
        path: "chart-builder/v2/:dashboardItemId?",
        name: "chart-builder-v2",
        component: ChartBuilder,
        meta: { requiresAuth: true }
      },

      {
        path: "page-builder/:page_id",
        name: "page-builder",
        component: PageBuilder,
        props: { isPageTemplate: false },
        meta: { requiresAuth: true }
      },

      {
        path: "page-template-builder/:page_id",
        name: "page-template-builder",
        component: PageBuilder,
        props: { isPageTemplate: true },
        meta: { requiresAuth: true }
      },

      {
        path: "dashboard-builder/:dashboard_id",
        name: "dashboard-builder",
        component: DashboardBuilder,
        props: { isDashboardTemplate: false},
        meta: { requiresAuth: true }
      },

      {
        path: "dashboard-template-builder/:dashboard_id",
        name: "dashboard-template-builder",
        component: DashboardBuilder,
        props: { isDashboardTemplate: true},
        meta: { requiresAuth: true }
      },

      {
        path: "files",
        name: "files",
        component: Files,
        meta: { requiresAuth: true }
      },

      {
        path: "merchants",
        name: "merchants",
        component: Merchants,
        meta: { requiresAuth: true }
      },

      {
        path: "rates-and-fees",
        name: "rates-and-fees",
        component: RateCard,
        meta: { requiresAuth: true }
      },

      {
        path: "statement-review",
        name: "statement-review",
        component: LineItemReview,
        meta: { requiresAuth: true }
      },

      {
        path: "pipelines",
        name: "pipelines",
        component: Pipelines,
        meta: { requiresAuth: true }
      },

      {
        path: "data-explorer",
        name: "data-explorer",
        component: DataExplorer,
        meta: { requiresAuth: true }
      },

      {
        path: "standardise",
        name: "standardise",
        component: Standardise,
        meta: { requiresAuth: true }
      },

      {
        path: "categorise",
        name: "categorise",
        component: Categorise,
        meta: { requiresAuth: true }
      },

      {
        path: "classify",
        name: "classify",
        component: Classify,
        meta: { requiresAuth: true }
      },

      {
        path: "interchange-fees",
        name: "interchange-fees",
        component: InterchangeFees,
        meta: { requiresAuth: true }
      },

      // {
      //   path: "pages",
      //   name: "pages",
      //   component: Pages,
      //   meta: { requiresAuth: true }
      // }

    ],
  },

  {
    path: "/dashboards",
    component: LayoutBackend,
    meta: { requiresAuth: true },
    children: [
      {
        path: "dashboard/:dashboard_id",
        name: "dashboard",
        component: DashboardRenderer,
        meta: { requiresAuth: true }
      }
    ],
  },

  {
    path: "/reports/:report_id",
    component: LayoutBackend,
    children: [
      {
        path: "page/:page_slug/:page_id",
        name: "report-page",
        component: BasePageRenderer,
        meta: { requiresAuth: true }
      },

      // {
      //   path: "page/bank-accounts",
      //   name: "page/bank-accounts",
      //   component: BankAccountsPage,
      //   meta: { requiresAuth: true }
      // },

      // {
      //   path: "foreign-exchange",
      //   name: "foreign-exchange",
      //   component: BasePageRenderer,
      //   meta: { requiresAuth: true }
      // },

      // {
      //   path: "other-products",
      //   name: "other-products",
      //   component: BasePageRenderer,
      //   meta: { requiresAuth: true }
      // },
    ],
  },
];

// Create Router
const router = createRouter({
  history: createWebHashHistory(),
  linkActiveClass: "active",
  linkExactActiveClass: "active",
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
  routes,
});

router.beforeEach(async (to) => {
  const authStore = useAuthUserStore();
  const requiresAuthentication = to.matched.some((record) => record.meta.requiresAuth);

  if (!requiresAuthentication) return true;

  // If we have no in-memory user (e.g. page refresh), attempt to restore the session
  // via the refresh token cookie before deciding whether to allow navigation.
  if (!authStore.getAuthenticatedUser) {
    const restored = await authStore.bootstrap();
    if (!restored) {
      return { path: "/login", query: { redirect: to.fullPath } };
    }
  }

  // Non-standard users must select a tenant before accessing protected pages.
  // bootstrap() already fetches the CubeJS token when tenantId is in sessionStorage,
  // so this only triggers when a tenant hasn't been selected yet.
  if (!authStore.getCubejsToken && to.name !== 'select-company') {
    return { name: 'select-company' };
  }
});

router.beforeResolve((to, from, next) => {
  next();
});

router.afterEach((to, from) => {

  // reset states if navigating away from the Chart builder
  if(from.name === 'chart-builder-v2'){
    const chartTypeStore = useChartTypeStore();
    const itemSettingsStore = useItemSettingsStore();

    // Reset chart settings
    itemSettingsStore.$reset()

    // Reset chart type selection
    chartTypeStore.$reset()
  }
  
});
/*eslint-enable no-unused-vars*/

export default router;
